<template>
  <v-container v-if="!permissionPending">
    <v-layout>
      <v-flex>
        <v-card
          v-if="selectedData.length === 0"
          class="ownerCard"
          :key="coOwnersKey">
          <v-list two-line>
            <v-subheader>Collaborators</v-subheader>
            <v-list-tile
              v-for="user in bulbCoOwners"
              :key="user.teacher_id"
              avatar
              >
              <v-list-tile-avatar>
                <img
                  v-if="user.userInfo.picture != null"
                  :src="user.userInfo.picture"
                  alt="avatar">
                <v-icon v-else large>account_circle</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="ownerList">
                <v-list-tile-title v-dompurify-html="user.userInfo.email"></v-list-tile-title>
                <v-list-tile-sub-title>{{ shareAccessRole(user.bulb_role) }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action class="actionSelect">
                <v-flex xs5 class="ownerAction">
                  <v-select
                  v-if="user.bulb_role !== 1"
                  :items="newAccessLevels"
                  v-model="user.bulb_role"
                  label="Select Access"
                  @change="setNewAccessLevel(user)"
                  :disabled="!isBulbOwner"
                  ></v-select>
                </v-flex>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout class="mt-3" align-center v-if="isBulbOwner">
    <v-flex xs10>
      <v-autocomplete
      v-model="localAddEmailList"
      hide-no-data
      hide-selected
      cache-items
      :items="matchingSchoolEmails"
      :label="accessLabel"
      item-text="email"
      item-value="id"
      :search-input.sync="emailSearchString"
      multiple
      chips
      deletable-chips
      :loading="searchingForEmailMatch"
      return-object
      :key="coOwnersKey"
      @input="clearEmailSearchString"
      append-icon=""
      small-chips
      >
      </v-autocomplete>
    </v-flex>
    <v-spacer></v-spacer>
    <v-flex>
      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn
            color="white"
            v-on="on"
            class="mr-0"
          >
            <div v-if="shareAccess === 'Can Edit'">
              <v-icon
                color="primary">
                create
              </v-icon>
              <v-icon
              color="primary">
              arrow_drop_down
            </v-icon>
            </div>
            <div v-else-if="shareAccess === 'Can View/Assign'">
              <v-icon
                color="primary">
                remove_red_eye
              </v-icon>
              <v-icon
                color="primary">
                arrow_drop_down
              </v-icon>
            </div>
            <div v-else-if="shareAccess === 'Make Owner'">
              <v-icon
                color="primary">
                person_add
              </v-icon>
              <v-icon
                color="primary">
                arrow_drop_down
              </v-icon>
            </div>
            <div v-else>
              <v-icon
                color="primary">
                person_add_disabled
              </v-icon>
              <v-icon
                color="primary">
                arrow_drop_down
              </v-icon>
            </div>
          </v-btn>
        </template>
        <v-list>
          <v-list-tile
            v-for="(role, index) in roles"
            :key="index"
            @click="setShareAccess(role)"
          >
            <v-list-tile-title>
              {{ role }}
            </v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-flex>
  </v-layout>
  <v-layout
    v-if="isBulbOwner">
    <v-flex>
      <v-btn
      color="primary"
      class="ma-0"
      @click="removeCoOwners"
      :disabled="localAddEmailList.length === 0 || searchingForEmailMatch"
      v-if="shareAccess === 'Remove Access'"
      >
        Remove Access
      </v-btn>
      <v-btn
      color="primary"
      class="ma-0"
      @click="submitAddCoOwners"
      :disabled="localAddEmailList.length === 0 || searchingForEmailMatch"
      v-else
      >
        Add Access
      </v-btn>
    </v-flex>
  </v-layout>
  </v-container>
  <v-container v-else>
    <p>Clicking transfer bulb(s) will transfer ownership of the bulb to another PlayPosit instructor. Once transferred, your role will update to a co-editor. <a
      href="https://knowledge.playposit.com/article/255-class-bulb-permissions#ownvsview"
      target="_blank">
      Click here
    </a> for more information on the abilities of a co-editor.</p>
  </v-container>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
import _ from 'lodash';

export default {
  name: 'BulbCollaborate',
  props: [
    'bulb_data',
  ],
  data() {
    return {
      emailMatchesSearched: false,
      emailSearchString: null,
      localAddEmailList: [],
      matchingSchoolEmails: [],
      newAccessLevels: [
        {
          text: 'Co-Editor',
          value: 2
        },
        {
          text: 'Facilitator',
          value: 3
        },
        {
          text: 'Remove Access',
          value: 0
        },
        {
          text: 'Make Owner',
          value: 1
        },
      ],
      originalCoOwners: [],
      shareAccess: 'Can Edit',
    };
  },
  computed: {
    ...mapState('instructors', [
      'addEmailList',
      'bulbCoOwners',
      'coOwnersKey',
      'emailMatchesFromSchool',
      'permissionPending',
      'searchingForEmailMatch',
      'selectedUser',
    ]),
    ...mapGetters([
      'instructorData',
      'school',
    ]),
    ...mapGetters('instructors', [
      'selectedData',
    ]),
    accessLabel() {
      if (this.shareAccess === 'Can Edit') return 'Enter email addresses to allow edit/share/view access.';
      if (this.shareAccess === 'Can View/Assign') return 'Enter email addresses to allow share/view access.';
      return 'Enter email addresses to remove access.';
    },
    shareAccessNumber() {
      if (this.shareAccess === 'Make Owner') return 1;
      if (this.shareAccess === 'Can Edit') return 2;
      if (this.shareAccess === 'Can View/Assign') return 3;
      if (this.shareAccess === 'Remove Access') return 0;
    },
    roles() {
      if (this.selectedData.length === 0) return ['Can Edit', 'Can View/Assign'];
      return ['Can Edit', 'Can View/Assign', 'Remove Access', 'Make Owner'];
    },
    isBulbOwner() {
      return this.bulb_data.bulb_role === 1;
    },
  },
  methods: {
    ...mapMutations([
      'triggerSnackbar',
    ]),
    ...mapMutations('instructors', [
      'setAddEmailList',
      'setPermissionPending',
      'setSearchingForEmailMatch',
      'setSelectedUser',
    ]),
    clearEmailSearchString() {
      this.emailSearchString = '';
    },
    setShareAccess(role) {
      this.shareAccess = role;
    },
    shareAccessRole(role) {
      if (role === 0) return 'Remove Access';
      if (role === 1) return 'Owner';
      if (role === 2) return 'Co-Editor - can edit/share/view';
      if (role === 3) return 'Facilitator - can share/view';
    },
    setNewAccessLevel(user) {
      if (user.bulb_role === 1) {
        this.setPermissionPending(true);
        this.setSelectedUser(user);
      } else {
        this.$store.dispatch('instructors/updateBulbCoOwners', { teacherId: user.teacher_id, accessLevel: user.bulb_role, bulbId: this.bulb_data.id });
      }
    },
    formatSchoolEmails() {
      this.matchingSchoolEmails = [];
      for (const match of this.emailMatchesFromSchool) {
        if (
          !_.find(this.bulbCoOwners, ['userInfo.email', match.email])
        ) {
          this.matchingSchoolEmails.push(match);
        }
      }
    },
    submitAddCoOwners() {
      if (this.shareAccessNumber === 1) {
        this.setPermissionPending(true);
      } else {
        const addUserGroup = {
          bulbs: this.selectedData.length > 0 ? this.selectedData : [{ id: this.bulb_data.id }],
          userEmails: this.localAddEmailList,
          accessLevel: this.shareAccessNumber
        };
        this.$store.dispatch('instructors/addBulbCoOwners', addUserGroup);
        this.localAddEmailList = [];
      }
    },
    removeCoOwners() {
      const removeUserGroup = {
        bulbs: this.selectedData,
        userEmails: this.localAddEmailList,
      };
      this.$store.dispatch('instructors/removeBulbCoOwners', removeUserGroup);
      this.localAddEmailList = [];
    },
  },
  watch: {
    'emailMatchesFromSchool'(n, o) {
      this.formatSchoolEmails();
    },
    'addEmailList'(n, o) {
      if (n.length > 10) {
        // 10 is the max number of co-owners that can be added at a time. - Pops the last name entered out and triggers snackbar.
        this.triggerSnackbar({ text: "Only 10 co-owners can be added or removed at a time.", color: 'warning', timeout: 5000 });
        this.addEmailList.pop();
      }
    },
    'emailSearchString'(n, o) {
      // reset email matches search if input is less than 4 characters
      if (n.length < 4) this.emailMatchesSearched = false;
      // Trigger email matches search when input = 4 characters and we haven't searched yet
      if (n.length > 3 && !this.emailMatchesSearched) {
        this.emailMatchesSearched = true;
        this.$store.dispatch('instructors/searchForEmailMatches', n);
      }
    },
    'permissionPending'(n, o) {
      if (n) {
        this.setAddEmailList(this.localAddEmailList);
        this.$store.commit('instructors/resetBulbCoOwners');
      } else {
        this.$store.dispatch('instructors/lookForBulbCoOwners', this.bulb_data.id);
      }
    },
  },
  mounted() {
    if (this.selectedData.length === 0) this.$store.dispatch('instructors/lookForBulbCoOwners', this.bulb_data.id);
  },
  destroyed() {
    this.$store.commit('instructors/resetBulbCoOwners');
  }
};
</script>

<style>
.ownerList {
  overflow: initial !important;
}

.actionSelect {
  max-width: 55% !important;
}

.ownerCard {
  max-height: 350px;
  overflow-y: auto;
}

.emailInput {
  width:100%;
}

.mainShareCard {
  min-height: 300px;
  background-color: #fff;
  overflow: auto;
}

.bold {
  font-weight: bold;
}

</style>
